import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link, useHistory } from 'react-router-dom'
import {
  Button,
  BUTTON_SIZE,
  BUTTON_THEME,
  CheckBox,
  CircularLoader as Loader,
  Icon,
  Select,
} from '@st-operations/uikit/src/v2'

import downloadCSV from '_lib/downloadCSV'
import { getCoverage, getTaxonomyCoverage } from '_services/api-integrations'

import { STATUS_CLASSES } from '../taxonomies/taxonomy/api-integrations/constants'

import {
  coverageReportCsv,
  coverageReportName,
  mappingLabel,
  noPushReason,
  notSyncedIn,
  number,
  percent,
  prefillFor,
  toPushIn,
  when,
} from '../taxonomies/taxonomy/api-integrations/coverage/format'
import '../taxonomies/taxonomy/api-integrations/coverage/styles.scss'

// rows a page, as for the pushes
const PAGE = 10

// How each column the table can be sorted by sorts, and which way it goes
// first: the worst first for a measure - the lowest share synced, the most
// not synced - A to Z for a name, and the oldest list first. A measure says
// nothing of a row not scanned yet, so those go last, whichever way.
const SORTS = {
  taxonomy: { first: 'asc', value: (row) => row.taxonomy?.name || '' },
  partner: { first: 'asc', value: (row) => row.partner?.name || '' },
  // the share synced - among equal shares, the most not synced first
  rate: {
    first: 'asc',
    measure: true,
    value: (row) => (row.counts.audiences ? row.counts.synced / row.counts.audiences : null),
    then: (a, b) => notSyncedIn(b) - notSyncedIn(a),
  },
  not_synced: { first: 'desc', measure: true, value: notSyncedIn },
  inactive: { first: 'desc', measure: true, value: (row) => row.counts.inactive || 0 },
  unchecked: { first: 'desc', measure: true, value: (row) => row.counts.unchecked || 0 },
  no_id: { first: 'desc', measure: true, value: (row) => row.counts.no_id || 0 },
  not_allowed: { first: 'desc', measure: true, value: (row) => row.counts.not_allowed || 0 },
  as_of: {
    first: 'asc',
    value: (row) => (row.snapshot?.taken_at ? new Date(row.snapshot.taken_at).getTime() : null),
  },
}

// the most not synced first, until another column is picked
const DEFAULT_SORT = { key: 'not_synced', dir: 'desc' }

// rows that sort alike go by taxonomy, partner and mapping ID
const byName = (a, b) =>
  (a.taxonomy?.name || '').localeCompare(b.taxonomy?.name || '') ||
  (a.partner?.name || '').localeCompare(b.partner?.name || '') ||
  String(a.mapping_id).localeCompare(String(b.mapping_id))

const sortRows = (rows, { key, dir }) => {
  const { measure, value, then } = SORTS[key]
  const valueOf = (row) => (measure && row.state !== 'ok' ? null : value(row))
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const x = valueOf(a)
    const y = valueOf(b)
    // nothing to sort by - last, whichever way
    if (x === null || y === null) {
      if (x === y) return byName(a, b)
      return x === null ? 1 : -1
    }
    const order = typeof x === 'string' ? x.localeCompare(y) : x - y
    return order * sign || (then ? then(a, b) : 0) || byName(a, b)
  })
}

// The table's columns - `sort` for those it can be sorted by
const COLUMNS = [
  { label: 'Taxonomy', sort: 'taxonomy' },
  { label: 'Partner', sort: 'partner' },
  { label: 'Mapping ID' },
  { label: 'Synced', sort: 'rate', title: 'Sorts by the share synced' },
  {
    label: 'Not synced',
    sort: 'not_synced',
    title: 'All but those synced - missing there, inactive, not checked, or with no ID',
  },
  { label: 'Inactive', sort: 'inactive' },
  { label: 'Not checked', sort: 'unchecked' },
  { label: 'No ID', sort: 'no_id' },
  { label: 'Not on allowlist', sort: 'not_allowed' },
  { label: 'As of', sort: 'as_of' },
]

const ARIA_SORT = { asc: 'ascending', desc: 'descending' }

// Which way a column sorts: both arrows faint until it is the one sorted by
const SortMark = ({ dir }) => (
  <svg className="coverage-sort__mark" width="8" height="12" viewBox="0 0 8 12" aria-hidden="true">
    <path className={dir === 'asc' ? 'is-on' : ''} d="M4 0l4 5H0z" />
    <path className={dir === 'desc' ? 'is-on' : ''} d="M4 12L0 7h8z" />
  </svg>
)

SortMark.propTypes = {
  dir: PropTypes.oneOf(['asc', 'desc']),
}

SortMark.defaultProps = {
  dir: null,
}

// A column's header - one it can be sorted by sorts the table when clicked,
// and turns it round when clicked again
const ColumnHeader = ({ column, sort, onSort }) => {
  if (!column.sort) return <th title={column.title}>{column.label}</th>
  const dir = sort.key === column.sort ? sort.dir : null
  return (
    <th aria-sort={ARIA_SORT[dir] || 'none'} title={column.title}>
      <button
        type="button"
        className={`coverage-sort${dir ? ' is-active' : ''}`}
        onClick={() => onSort(column.sort)}
      >
        {column.label}
        <SortMark dir={dir} />
      </button>
    </th>
  )
}

ColumnHeader.propTypes = {
  column: PropTypes.shape({
    label: PropTypes.string,
    sort: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  sort: PropTypes.shape({ key: PropTypes.string, dir: PropTypes.string }).isRequired,
  onSort: PropTypes.func.isRequired,
}

// How the scan went, as the pushes show how theirs went
const SCAN_STATES = {
  running: { label: 'Running', className: STATUS_CLASSES.running },
  done: { label: 'Finished', className: STATUS_CLASSES.completed },
  failed: { label: 'Failed', className: STATUS_CLASSES.failed },
}

// When the last scan ran, and how each partner's list went - a partner whose
// list failed carries on from the one before, which its rows' dates show. In
// a panel of its own above the filters, each a label over its value.
const ScanSummary = ({ scan, partners }) => (
  <div className="coverage-scan">
    <Choose>
      <When condition={!scan}>
        <div className="is-quiet">
          No scan yet. One runs once a day, soon after midnight Pacific - and soon after the API
          starts, when today&apos;s has not run.
        </div>
      </When>
      <Otherwise>
        <dl className="coverage-scan__stats">
          <div className="coverage-scan__stat">
            <dt>Last scan</dt>
            <dd>
              {when(scan.started_at)}
              <span className={`status-pill ${SCAN_STATES[scan.status]?.className || ''}`}>
                {SCAN_STATES[scan.status]?.label || scan.status}
              </span>
            </dd>
          </div>
          <For each="partner" of={partners}>
            <div key={partner.slug} className="coverage-scan__stat">
              <dt>{partner.name}</dt>
              <dd>
                <Choose>
                  {/* AppNexus's is how many of ours it has - it is asked about
                      our IDs rather than listing everything */}
                  <When condition={scan.partners?.[partner.slug]?.state === 'done'}>
                    {number(scan.partners[partner.slug].count)} segments
                  </When>
                  <When condition={scan.partners?.[partner.slug]?.state === 'failed'}>
                    <span className="is-bad">failed - {scan.partners[partner.slug].error}</span>
                  </When>
                  <Otherwise>
                    <span className="is-quiet">not listed yet</span>
                  </Otherwise>
                </Choose>
              </dd>
            </div>
          </For>
        </dl>
        <If condition={scan.error}>
          <div className="coverage-scan__error is-bad">{scan.error}</div>
        </If>
      </Otherwise>
    </Choose>
  </div>
)

ScanSummary.propTypes = {
  scan: PropTypes.object,
  partners: PropTypes.arrayOf(PropTypes.shape({ slug: PropTypes.string, name: PropTypes.string }))
    .isRequired,
}

ScanSummary.defaultProps = {
  scan: null,
}

// Turning while a row's action works
const Spinner = () => <span className="coverage-action__spinner" aria-hidden="true" />

// A row's two actions, as icon buttons with tooltips - beside each other, the
// report always on the right. Sync arrows for the push, as the columns speak
// of synced and not, and a CSV file for the report, which is one:
//
//   push     a new push of the audiences its partner does not have - those
//            left off the allowlist included, none on a blocklist (the API's
//            to_push) - in the same prefilled push form as the taxonomy
//            page's "Push these", to check and run
//   report   every audience in its taxonomy, synced in the partner or not, as
//            a CSV
//
// Both work the taxonomy out afresh first - the row's counts are the last
// scan's and carry no audiences - so anything pushed since is left out of a
// push, and counted in a report. The button spins while it does; anything
// that came of it other than what was asked is said beneath.
const RowActions = ({ row }) => {
  const history = useHistory()
  const [working, setWorking] = useState(null)
  const [note, setNote] = useState(null)
  const reason = noPushReason(row)
  const toPush = toPushIn(row)

  // this row as the taxonomy's coverage has it now
  const freshRow = async (...params) => {
    const fresh = await getTaxonomyCoverage(row.taxonomy._id, ...params)
    return (fresh?.items || []).find((item) => item.key === row.key)
  }

  const push = async () => {
    setWorking('push')
    setNote(null)
    try {
      const now = await freshRow()
      if (now?.to_push?.length) {
        // off to the push form - nothing more to show here
        history.push({
          pathname: `/taxonomies/${row.taxonomy.slug}/api-integrations/new`,
          state: { prefill: prefillFor(now, now.to_push) },
        })
        return
      }
      setNote('Nothing to push now')
    } catch (e) {
      setNote(e?.message || 'could not work out what is missing')
    }
    setWorking(null)
  }

  const report = async () => {
    setWorking('report')
    setNote(null)
    try {
      const now = await freshRow({ audiences: 'all' })
      if (now?.state === 'ok') {
        downloadCSV(coverageReportCsv(now), coverageReportName(row.taxonomy.name, now))
      } else {
        setNote(now ? 'Not scanned yet' : 'No longer in the taxonomy - reload the page')
      }
    } catch (e) {
      setNote(e?.message || 'could not make the report')
    }
    setWorking(null)
  }

  // the tooltip's words: what it does, or why it cannot, or what it is doing
  const pushTip =
    (working === 'push' && 'Working out what is missing…') ||
    reason ||
    `Push the ${number(toPush)} unsynced audiences to ${row.partner.name}`
  const reportTip =
    working === 'report'
      ? 'Preparing the report…'
      : 'Download a report of every audience, synced or not, as a CSV'

  return (
    <div className="coverage-actions">
      <div className="coverage-actions__buttons">
        <If condition={toPush > 0}>
          <button
            type="button"
            className="coverage-action de-200px-tooltip-wrap"
            onClick={push}
            disabled={Boolean(working) || Boolean(reason)}
            aria-label="Push unsynced"
            aria-busy={working === 'push'}
            data-tooltip-text={pushTip}
            data-tooltip-direction="right"
          >
            <Choose>
              <When condition={working === 'push'}>
                <Spinner />
              </When>
              <Otherwise>
                <Icon
                  icon="synchronize"
                  type="fill"
                  width="20px"
                  height="20px"
                  color="currentColor"
                />
              </Otherwise>
            </Choose>
          </button>
        </If>
        <button
          type="button"
          className="coverage-action de-200px-tooltip-wrap"
          onClick={report}
          disabled={Boolean(working)}
          aria-label="Download report"
          aria-busy={working === 'report'}
          data-tooltip-text={reportTip}
          data-tooltip-direction="right"
        >
          <Choose>
            <When condition={working === 'report'}>
              <Spinner />
            </When>
            <Otherwise>
              <Icon icon="filecsv" type="fill" width="17px" height="17px" color="currentColor" />
            </Otherwise>
          </Choose>
        </button>
      </div>
      <If condition={note}>
        <div className="coverage-actions__note">{note}</div>
      </If>
    </div>
  )
}

RowActions.propTypes = {
  row: PropTypes.shape({
    key: PropTypes.string,
    taxonomy: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    partner: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
}

// a distribution with audiences its partner does not have active - one not
// scanned yet has nothing to say
const hasUnsynced = (row) => row.state === 'ok' && notSyncedIn(row) > 0

// Every taxonomy's coverage in every partner we scan, from the last nightly
// scan - one row per taxonomy, partner and mapping ID, sorted by any column
// (the most not synced first to begin with) and filtered by taxonomy, by
// partner and by mapping ID. A row's taxonomy page lists the audiences behind
// its counts; a row with audiences missing from its partner can start a push
// of them here.
const Coverage = () => {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [taxonomy, setTaxonomy] = useState('')
  const [partner, setPartner] = useState('')
  const [mappingId, setMappingId] = useState('')
  const [onlyUnsynced, setOnlyUnsynced] = useState(false)
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [skip, setSkip] = useState(0)

  // a column picked again turns round; a new one starts its own way
  const onSort = (key) => {
    setSkip(0)
    setSort(
      sort.key === key
        ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: SORTS[key].first }
    )
  }

  useEffect(() => {
    getCoverage()
      .then((answer) => setData(answer))
      .catch((e) => setError(e?.message || 'could not load coverage'))
  }, [])

  if (error) return <div className="text-red-600 mb-4">{error}</div>
  if (!data) {
    return (
      <div className="api-integrations-loading">
        <Loader />
      </div>
    )
  }

  const partners = data.partners || []
  const rows = sortRows(
    (data.items || [])
      .filter((row) => !taxonomy || row.taxonomy?._id === taxonomy)
      .filter((row) => !partner || row.partner?.slug === partner)
      .filter((row) => !mappingId || row.mapping_id === mappingId)
      .filter((row) => !onlyUnsynced || hasUnsynced(row)),
    sort
  )
  const page = rows.slice(skip, skip + PAGE)
  const shown = page.length ? `${skip + 1}–${skip + page.length} of ${rows.length}` : '0 of 0'

  // the taxonomies there is coverage for, A to Z
  const named = new Map(
    (data.items || [])
      .filter((row) => row.taxonomy?._id)
      .map((row) => [row.taxonomy._id, row.taxonomy.name || row.taxonomy._id])
  )
  const taxonomyOptions = [
    { value: '', label: 'Every taxonomy' },
    ...[...named]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ]
  const partnerOptions = [
    { value: '', label: 'Every partner' },
    ...partners.map((p) => ({ value: p.slug, label: p.name })),
  ]
  // the mapping IDs there is coverage for, A to Z - as the table shows them
  const mappingIdOptions = [
    { value: '', label: 'Every mapping ID' },
    ...[...new Set((data.items || []).map((row) => row.mapping_id).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
      .map((code) => ({ value: code, label: code })),
  ]

  return (
    <div>
      <ScanSummary scan={data.scan} partners={partners} />

      <div className="push-filters">
        <div className="push-filters__field">
          <Select
            useReactSelect
            isSearchable
            name="taxonomy"
            label="Taxonomy"
            value={taxonomyOptions.find((o) => o.value === taxonomy) || ''}
            options={taxonomyOptions}
            onChange={(option, name, val) => {
              setSkip(0)
              setTaxonomy(val || '')
            }}
          />
        </div>
        <div className="push-filters__field">
          <Select
            useReactSelect
            name="partner"
            label="Partner"
            value={partnerOptions.find((o) => o.value === partner) || ''}
            options={partnerOptions}
            onChange={(option, name, val) => {
              setSkip(0)
              setPartner(val || '')
            }}
          />
        </div>
        <div className="push-filters__field">
          <Select
            useReactSelect
            isSearchable
            name="mapping_id"
            label="Mapping ID"
            value={mappingIdOptions.find((o) => o.value === mappingId) || ''}
            options={mappingIdOptions}
            onChange={(option, name, val) => {
              setSkip(0)
              setMappingId(val || '')
            }}
          />
        </div>
        <div className="coverage-unsynced-only">
          <CheckBox
            checked={onlyUnsynced}
            onChange={() => {
              setSkip(0)
              setOnlyUnsynced(!onlyUnsynced)
            }}
            label="Only show distributions with unsynced audiences"
          />
        </div>
      </div>

      <Choose>
        <When condition={!rows.length}>
          <div className="text-gray-500">
            <Choose>
              <When condition={taxonomy || partner || mappingId || onlyUnsynced}>
                Nothing matches these filters.
              </When>
              <Otherwise>
                No taxonomy distributes to a partner we scan yet - or the first scan has not run.
              </Otherwise>
            </Choose>
          </div>
        </When>
        <Otherwise>
          <table className="coverage-table coverage-table--overview">
            <thead>
              <tr>
                <For each="column" of={COLUMNS}>
                  <ColumnHeader key={column.label} column={column} sort={sort} onSort={onSort} />
                </For>
                <th aria-label="Push" />
              </tr>
            </thead>
            <tbody>
              <For each="row" of={page}>
                <tr key={row._id}>
                  <td>
                    <Link to={`/taxonomies/${row.taxonomy._id}`}>{row.taxonomy.name}</Link>
                  </td>
                  <td>{row.partner.name}</td>
                  <td>
                    <div>{mappingLabel(row)}</div>
                    <div className="is-quiet">{row.destinations.map((d) => d.name).join(', ')}</div>
                  </td>
                  <Choose>
                    <When condition={row.state !== 'ok'}>
                      <td colSpan={6} className="is-quiet">
                        not scanned yet{row.problem ? ` - ${row.problem}` : ''}
                      </td>
                    </When>
                    <Otherwise>
                      <td>
                        {number(row.counts.synced)} of {number(row.counts.audiences)}{' '}
                        <span className="is-quiet">
                          ({percent(row.counts.synced, row.counts.audiences)})
                        </span>
                      </td>
                      {/* all but those synced; the next three are among them */}
                      <td className={notSyncedIn(row) ? 'is-warn' : ''}>
                        {number(notSyncedIn(row))}
                      </td>
                      <td className={row.counts.inactive ? 'is-warn' : ''}>
                        {number(row.counts.inactive)}
                      </td>
                      <td>{number(row.counts.unchecked)}</td>
                      <td>{number(row.counts.no_id)}</td>
                      <td className={row.counts.not_allowed ? 'is-warn' : ''}>
                        {number(row.counts.not_allowed)}
                      </td>
                    </Otherwise>
                  </Choose>
                  <td className="is-quiet">{when(row.snapshot?.taken_at)}</td>
                  <td>
                    <If condition={row.state === 'ok'}>
                      <RowActions row={row} />
                    </If>
                  </td>
                </tr>
              </For>
            </tbody>
          </table>

          <div className="pager pager--list">
            <span className="meta">{shown}</span>
            <div className="flex gap-2">
              <Button
                theme={BUTTON_THEME.SECONDARY}
                size={BUTTON_SIZE.SMALL}
                onClick={() => setSkip(Math.max(0, skip - PAGE))}
                disabled={skip === 0}
              >
                Previous
              </Button>
              <Button
                theme={BUTTON_THEME.SECONDARY}
                size={BUTTON_SIZE.SMALL}
                onClick={() => setSkip(skip + PAGE)}
                disabled={skip + PAGE >= rows.length}
              >
                Next
              </Button>
            </div>
          </div>
        </Otherwise>
      </Choose>
    </div>
  )
}

export default Coverage
